import { atom, computed } from 'nanostores';
import { chatState } from './chatState';

/**
 * Ref-counted pending file writes.
 * Incremented before each `updateFileActionsInDb` call begins (including
 * concurrent calls from `#processEventBuffer`), decremented in the `finally`
 * block when the write settles.
 *
 * Stays > 0 as long as any file write is in-flight.
 */
export const filesIdbPending = atom<number>(0);

/**
 * Set to `true` once `storeMessageHistory` resolves for the current streaming
 * round, or immediately when there are no new messages to persist.
 *
 * Reset to `false` at the start of each new stream (on `submitted`).
 *
 * NOTE: We intentionally do NOT set this to `true` on IDB write errors —
 * auto-save stays blocked but manual save via SavingCard still works.
 */
export const messagesIdbReady = atom<boolean>(false);

/**
 * Combined gate: true only when filesIdbPending reaches 0 AND messages are
 * persisted. This is the single signal ChatView watches before auto-saving.
 */
export const idbReadyForUpload = computed(
  [filesIdbPending, messagesIdbReady],
  (files, msgs) => files === 0 && msgs,
);

/** Call BEFORE scheduling any file write (before debounce timers too). */
export function beginFileWrite() {
  filesIdbPending.set(filesIdbPending.get() + 1);
}

/** Call in the `finally` block of the write — always, even on error. */
export function endFileWrite() {
  // Math.max guards against accidental double-decrement bugs
  filesIdbPending.set(Math.max(0, filesIdbPending.get() - 1));
}

/**
 * Reset both signals at the start of each new user message so gates are closed
 * before the next streaming round completes.
 *
 * Resetting filesIdbPending to 0 also recovers from any counter leaks (e.g.
 * a beginFileWrite without a matching endFileWrite from the previous round).
 * Any orphaned endFileWrite() calls from the previous round will clamp
 * harmlessly via Math.max(0, ...) in endFileWrite.
 */
chatState.subscribe(({ status }) => {
  if (status === 'submitted' || status === 'streaming') {
    messagesIdbReady.set(false);
    filesIdbPending.set(0);
  }
});
